@let isMaximized = (isEditorMaximized$ | async) ?? false;
<div class="top-bar">
  @if (!isMaximized) {
    <div class="tabs">
      <div [class.active]="currentTab === 'EDITOR'" (click)="switchTab('EDITOR')" class="clickable tab" translate="CALLS.EDITOR"></div>
      @if (call.isOutlineReady) {
        <div [class.active]="currentTab === 'OUTLINE'" (click)="switchTab('OUTLINE')" class="clickable tab" translate="CALLS.OUTLINE"></div>
      }
    </div>
  }
  @if (call.isPostProcessingComplete) {
    <div class="buttons">
      @if (currentTab === 'OUTLINE') {
        @if (showExpandAll) {
          <button mat-icon-button (click)="expandAll()">
            <mat-icon fontSet="material-symbols-outlined">expand_all</mat-icon>
          </button>
        } @else {
          <button mat-icon-button (click)="collapseAll()">
            <mat-icon fontSet="material-symbols-outlined">collapse_all</mat-icon>
          </button>
        }
      }
      @if (canUserEdit && currentTab === 'EDITOR' && call.useLegacyEditor) {
        <button mat-icon-button (click)="showRevisionHistory()" [matTooltip]="'CALLS.SHOW_REVISION_HISTORY' | translate" [disabled]="isEditorLockedByOtherUser">
          <mat-icon>timeline</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && !call.useLegacyEditor && isMaximized) {
        @let zoomLevel = (zoomLevel$ | async) ?? 1;
        <button
          mat-icon-button
          (click)="zoomOut()"
          [disabled]="zoomLevel === editorZoomMin"
          [attr.aria-label]="'EDITOR.SETTINGS.ZOOM_OUT' | translate"
          [matTooltip]="'EDITOR.SETTINGS.ZOOM_OUT' | translate"
        >
          <mat-icon>zoom_out</mat-icon>
        </button>
        <button
          mat-button
          class="zoom-indicator"
          (click)="resetZoom()"
          [attr.aria-label]="'EDITOR.SETTINGS.RESET_ZOOM' | translate"
          [matTooltip]="'EDITOR.SETTINGS.RESET_ZOOM' | translate"
        >
          {{ zoomLevel * 100 | number: '1.0-0' }}%
        </button>
        <button
          mat-icon-button
          (click)="zoomIn()"
          [disabled]="zoomLevel === editorZoomMax"
          [attr.aria-label]="'EDITOR.SETTINGS.ZOOM_IN' | translate"
          [matTooltip]="'EDITOR.SETTINGS.ZOOM_IN' | translate"
        >
          <mat-icon>zoom_in</mat-icon>
        </button>
      }
      @if (canUserEdit && currentTab === 'EDITOR' && !call.useLegacyEditor) {
        <button
          mat-icon-button
          class="track-changes-toggle"
          [class.active]="$safeNavigationMigration(tiptapEditor?.trackChangesEnabled$) | async"
          [matTooltip]="($safeNavigationMigration(tiptapEditor?.trackChangesToggleLabel$) | async) ?? ''"
          [attr.aria-label]="($safeNavigationMigration(tiptapEditor?.trackChangesToggleLabel$) | async) ?? ''"
          (click)="tiptapEditor?.toggleTrackChanges()"
        >
          <mat-icon>track_changes</mat-icon>
        </button>
      }
      @if (canUserEdit && currentTab === 'EDITOR') {
        <button
          mat-icon-button
          (click)="resetEditor()"
          [matTooltip]="'CALLS.RESET_EDITOR' | translate"
          [disabled]="call.useLegacyEditor && isEditorLockedByOtherUser"
        >
          <mat-icon>restart_alt</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && hasCitations) {
        <button
          mat-icon-button
          [class.active]="showCitations"
          [attr.aria-pressed]="showCitations"
          (click)="toggleCitations()"
          [matTooltip]="(showCitations ? 'EDITOR.SETTINGS.HIDE_CITATIONS' : 'EDITOR.SETTINGS.SHOW_CITATIONS') | translate"
        >
          <mat-icon>link</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR') {
        <button
          mat-icon-button
          [class.active]="showWordCount"
          [attr.aria-pressed]="showWordCount"
          (click)="toggleWordCount()"
          [matTooltip]="(showWordCount ? 'EDITOR.SETTINGS.HIDE_WORD_COUNT' : 'EDITOR.SETTINGS.SHOW_WORD_COUNT') | translate"
        >
          <mat-icon>title</mat-icon>
        </button>
      }
      @if (canExportCurrentTab) {
        <button mat-icon-button [matMenuTriggerFor]="menu">
          <mat-icon>download</mat-icon>
        </button>
        <mat-menu #menu="matMenu">
          <button mat-menu-item (click)="exportDocument('pdf')">PDF</button>
          <button mat-menu-item (click)="exportDocument('docx')">DOCX</button>
        </mat-menu>
      }
      @if (currentTab === 'EDITOR') {
        <button
          mat-icon-button
          class="maximize-toggle"
          [class.active]="isMaximized"
          [attr.aria-pressed]="isMaximized"
          [attr.aria-label]="(isMaximized ? 'EDITOR.SETTINGS.RESTORE_EDITOR' : 'EDITOR.SETTINGS.MAXIMIZE_EDITOR') | translate"
          (click)="toggleEditorMaximized()"
          [matTooltip]="(isMaximized ? 'EDITOR.SETTINGS.RESTORE_EDITOR' : 'EDITOR.SETTINGS.MAXIMIZE_EDITOR') | translate"
        >
          <mat-icon>{{ isMaximized ? 'close_fullscreen' : 'fullscreen' }}</mat-icon>
        </button>
      }
    </div>
  }
</div>

<div class="content">
  @if (isSensitiveModeEnabled) {
    <div class="sensitive-mode-placeholder"></div>
  } @else if (currentTab === 'EDITOR') {
    @if (!call.useLegacyEditor) {
      <app-tiptap-editor
        [call]="call"
        [canUserEdit]="canUserEdit"
        [canUserComment]="canUserComment"
        (openAgendaUpload)="openAgendaUpload.emit()"
        (openDocument)="relayOpenDocument($event)"
        (openMeeting)="relayOpenMeeting()"
      ></app-tiptap-editor>
    } @else {
      <app-editor
        [call]="call"
        [canUserEdit]="canUserEdit"
        [canUserComment]="canUserComment"
        (isLocked)="isEditorLockedByOtherUser = $event"
        (openAgendaUpload)="openAgendaUpload.emit()"
        (openDocument)="relayOpenDocument($event)"
        (openMeeting)="relayOpenMeeting()"
      ></app-editor>
    }
  } @else if (currentTab === 'OUTLINE') {
    <app-outline [call]="call"></app-outline>
  }
</div>
